<script lang="ts">
	/**
	 * Skeleton shaped like an entity card — image, text block, byline, optional
	 * button. Pairs with `List.svelte`, so a loading grid occupies the same
	 * footprint the real cards will.
	 *
	 * Every region is toggleable because the whole value of a card skeleton is
	 * matching the card you are actually about to render; a mismatched one
	 * causes exactly the reflow it was meant to prevent.
	 */

	/** Width cap, matching Flowbite's placeholder sizes. */
	export let size: 'sm' | 'md' | 'lg' | 'xl' | 'xxl' = 'sm';

	export let showImage: boolean = true;
	export let showText: boolean = true;
	export let showUser: boolean = true;
	export let showButton: boolean = false;

	/** Lay the image beside the text instead of above it. */
	export let horizontal: boolean = false;

	/** Image box dimensions as Tailwind classes (passed whole for the JIT scanner). */
	export let imageSizeClass: string = 'h-48 w-48';
	export let paddingClass: string = 'p-4 md:p-6';
	export let buttonClass: string = '';
	export let divClass: string = '';

	let className: string = '';
	export { className as class };

	const sizes: Record<string, string> = {
		sm: 'max-w-sm',
		md: 'max-w-md',
		lg: 'max-w-lg',
		xl: 'max-w-xl',
		xxl: 'max-w-2xl'
	};

	const barClass = 'bg-gray-300 rounded-full dark:bg-gray-700';
</script>

<div
	role="status"
	class="rounded-2xl border-widget shadow animate-pulse {paddingClass} {sizes[size]} {divClass} {className} {horizontal
		? 'flex gap-5'
		: ''}"
>
	{#if showImage}
		<div
			class="flex justify-center items-center {!horizontal ? 'mb-4' : ''} {imageSizeClass} shrink-0 bg-gray-300 rounded dark:bg-gray-700"
		>
			<svg
				width="48"
				height="48"
				class="text-gray-400 dark:text-gray-600"
				xmlns="http://www.w3.org/2000/svg"
				aria-hidden="true"
				fill="currentColor"
				viewBox="0 0 640 512"
			>
				<path
					d="M480 80C480 35.82 515.8 0 560 0C604.2 0 640 35.82 640 80C640 124.2 604.2 160 560 160C515.8 160 480 124.2 480 80zM0 456.1C0 445.6 2.964 435.3 8.551 426.4L225.3 81.01C231.9 70.42 243.5 64 256 64C268.5 64 280.1 70.42 286.8 81.01L412.7 281.7L460.9 202.7C464.1 196.1 472.2 192 480 192C487.8 192 495 196.1 499.1 202.7L631.1 419.1C636.9 428.6 640 439.7 640 450.9C640 484.6 612.6 512 578.9 512H55.91C25.03 512 .0006 486.1 .0006 456.1L0 456.1z"
				/>
			</svg>
		</div>
	{/if}

	<div class="w-full">
		{#if showText}
			<div class="h-2.5 {barClass} w-1/2 mb-4" />
			<div class="h-2 {barClass} mb-2.5" />
			<div class="h-2 {barClass} mb-2.5" />
			<div class="h-2 {barClass}" />
		{/if}

		{#if showUser}
			<div class="flex items-center mt-4 space-x-3">
				<svg
					class="w-14 h-14 text-gray-300 dark:text-gray-700"
					aria-hidden="true"
					fill="currentColor"
					viewBox="0 0 20 20"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						fill-rule="evenodd"
						d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z"
						clip-rule="evenodd"
					/>
				</svg>
				<div>
					<div class="h-2.5 {barClass} w-32 mb-2" />
					<div class="w-48 h-2 {barClass}" />
				</div>
			</div>
		{/if}

		{#if showButton}
			<div
				class="flex items-center justify-center mt-4 space-x-3 p-4 rounded-2xl border-widget shadow animate-pulse md:p-6 {buttonClass}"
			>
				<div class="h-2.5 {barClass} w-1/2" />
			</div>
		{/if}
		<slot />
	</div>
	<span class="sr-only">Loading...</span>
</div>
